import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaArrowLeft, FaArrowRight, FaCheck, FaCode, FaCopy, FaDownload, FaFilter, FaLayerGroup, FaSearch, FaTable, FaTimes } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import Select from 'react-select';
import DeviceService from '../services/DeviceService';
import TelemetryImagePreview from './TelemetryImagePreview';
import { compareTelemetry, exactValue, fieldType, filterTelemetry, flattenTelemetry, formatCaptureTime, isImageRecord, normalizeTelemetryRecords, sectionLabel, telemetryCsv, telemetryImageSource } from '../utils/deviceTelemetry';
import '../styles/SupportDashboard.css';
import '../styles/DeviceTelemetryPage.css';

const inputDate = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const initialDate = (daysAgo = 0) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return inputDate(date);
};
const apiDate = (value, end = false) => {
  if (!value) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setHours(end ? 23 : 0, end ? 59 : 0, end ? 59 : 0, end ? 999 : 0);
  return date.toISOString();
};
const PAGE_SIZE = 100;

export default function DeviceTelemetryPage({ embedded = false }) {
  const location = useLocation();
  const seededVisorRef = location.state?.visorRef || '';
  const [visorRef, setVisorRef] = useState(seededVisorRef);
  const [fromDate, setFromDate] = useState(() => initialDate(7));
  const [toDate, setToDate] = useState(() => initialDate());
  const [records, setRecords] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [comparisonId, setComparisonId] = useState('');
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('');
  const [type, setType] = useState('');
  const [hideEmpty, setHideEmpty] = useState(false);
  const [changedOnly, setChangedOnly] = useState(false);
  const [view, setView] = useState('fields');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loadedReference, setLoadedReference] = useState('');
  const noticeTimer = useRef();
  const requestId = useRef(0);
  const tableScroll = useRef(null);

  useEffect(() => { if (seededVisorRef) setVisorRef(seededVisorRef); }, [seededVisorRef]);
  useEffect(() => () => { clearTimeout(noticeTimer.current); requestId.current += 1; }, []);

  const selectedIndex = Math.max(0, records.findIndex((record) => record.id === selectedId));
  const selectedRecord = records[selectedIndex];
  const comparison = records.find((record) => record.id === comparisonId && record.id !== selectedRecord?.id);
  const fields = useMemo(() => selectedRecord ? compareTelemetry(selectedRecord.payload, comparison?.payload) : [], [selectedRecord, comparison]);
  const totalFields = useMemo(() => selectedRecord ? flattenTelemetry(selectedRecord.payload).length : 0, [selectedRecord]);
  const sections = useMemo(() => {
    const counts = new Map();
    fields.forEach((field) => counts.set(field.group, (counts.get(field.group) || 0) + 1));
    return [...counts].sort(([a], [b]) => a.localeCompare(b));
  }, [fields]);
  const filteredFields = useMemo(() => filterTelemetry(fields, {
    query, group, type, hideEmpty, changedOnly: !!comparison && changedOnly,
  }), [fields, query, group, type, hideEmpty, changedOnly, comparison]);
  const changes = fields.filter((field) => field.change && field.change !== 'unchanged').length;
  const totalPages = Math.max(1, Math.ceil(filteredFields.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleFields = filteredFields.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const deviceLabel = selectedRecord?.entry?.visorId || selectedRecord?.payload?.user_id || loadedReference;
  const rawJson = selectedRecord ? JSON.stringify(selectedRecord.payload, null, 2) : '';
  const activeFilters = !!(query || group || type || hideEmpty || (comparison && changedOnly));

  useEffect(() => { setPage(1); }, [query, group, type, hideEmpty, changedOnly, selectedId, comparisonId]);
  useEffect(() => { if (tableScroll.current) tableScroll.current.scrollTop = 0; }, [currentPage, query, group, type, hideEmpty, changedOnly, selectedId, comparisonId]);

  const notify = (message) => {
    clearTimeout(noticeTimer.current);
    setNotice(message);
    noticeTimer.current = setTimeout(() => setNotice(''), 3500);
  };
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(`${label} copied.`);
    } catch {
      notify('Clipboard unavailable. Select and copy the text manually.');
    }
  };
  const download = (content, extension, mime) => {
    const safeReference = String(deviceLabel || 'device').replace(/[^a-zA-Z0-9_-]/g, '_');
    const capture = selectedRecord?.timestamp ? new Date(selectedRecord.timestamp).toISOString().replace(/[:.]/g, '-') : 'unknown-time';
    const url = URL.createObjectURL(new Blob([content], { type: mime }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${safeReference}-telemetry-${capture}.${extension}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    notify(`${extension.toUpperCase()} export ready.`);
  };
  const resetFilters = () => { setQuery(''); setGroup(''); setType(''); setHideEmpty(false); setChangedOnly(false); };
  const chooseRecord = (id) => {
    setSelectedId(id);
    if (comparisonId === id) { setComparisonId(''); setChangedOnly(false); }
    setGroup('');
  };
  const submit = async (event) => {
    event?.preventDefault();
    if (loading) return;
    if (!visorRef.trim()) { setError('Enter a visor ref or device reference to search.'); return; }
    if (fromDate && toDate && fromDate > toDate) { setError('The from date cannot be after the to date.'); return; }
    const id = ++requestId.current;
    setLoading(true);
    setError('');
    setNotice('');
    setHasSearched(true);
    setRecords([]);
    setSelectedId('');
    setComparisonId('');
    resetFilters();
    setView('fields');
    try {
      const result = await DeviceService.getDeviceTelemetry({ visorRef: visorRef.trim(), fromDate: apiDate(fromDate), toDate: apiDate(toDate, true) });
      if (id !== requestId.current) return;
      const normalized = normalizeTelemetryRecords(result);
      setRecords(normalized);
      setSelectedId(normalized[0]?.id || '');
      setLoadedReference(visorRef.trim());
    } catch (ex) {
      if (id !== requestId.current) return;
      setError(ex?.status === 404 ? 'No telemetry records were found for that search.' : ex?.body?.message || ex?.message || 'Telemetry could not be loaded. Please try again.');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };
  const recordLabel = (record, index) => {
    const captureType = record.entry.recordType ?? record.entry.RecordType ?? record.entry.record_type ?? record.entry.type ??
      record.payload.recordType ?? record.payload.RecordType ?? record.payload.record_type ?? record.payload.type;
    const typeNames = { 1: 'Image', 2: 'Heartbeat' };
    const typeLabel = ['1', '2'].includes(String(captureType)) ? `Type ${captureType} (${typeNames[captureType]})` : 'Type unknown';
    const title = index === 0 ? 'Latest' : `Record ${index + 1}`;
    const time = formatCaptureTime(record.timestamp);
    return { value: record.id, label: `${title} · ${typeLabel} · ${time}`, title, time, typeLabel, captureType };
  };

  const captureOptions = records.map(recordLabel);
  const formatCaptureOption = (option) => option.title ? (
    <span className="dt-capture-option"><span>{option.title}</span>
      <span className={`dt-capture-pill dt-capture-pill-${option.captureType}`}>{option.typeLabel}</span>
      <span className="dt-capture-time">{option.time}</span></span>
  ) : option.label;
  const captureSelectProps = {
    className: 'dt-capture-select', classNamePrefix: 'dt-capture',
    formatOptionLabel: formatCaptureOption, isSearchable: false,
    unstyled: true,
  };

  const content = (
    <div className={`sd-telemetry-app ${embedded ? 'sd-telemetry-app-embedded' : ''}`}>
      <main className="dt-content">
        <header className="dt-heading">
          <div><div className="dt-eyebrow"><FaLayerGroup /> Metadata inspector</div>
            <h1>Device telemetry</h1><p>Explore device observations and see what changed between captures.</p></div>
          <span className="dt-readonly">Read only</span>
        </header>
        <form className="dt-search-panel" onSubmit={submit} aria-label="Find telemetry">
          <label className="dt-device-search"><span>Visor ref / device ID</span>
            <div className="dt-search-input"><FaSearch aria-hidden="true" /><input value={visorRef} onChange={(e) => setVisorRef(e.target.value)} placeholder="e.g. testuser-3-cs" autoComplete="off" required />
              {visorRef && <button type="button" onClick={() => setVisorRef('')} aria-label="Clear device reference"><FaTimes /></button>}</div></label>
          <label><span>From</span><input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} /></label>
          <label><span>To</span><input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} /></label>
          <button className="dt-button dt-primary" type="submit" disabled={loading}><FaSearch /> {loading ? 'Searching…' : 'Search records'}</button>
        </form>
        {error && <div className="dt-alert" role="alert">{error}</div>}
        <div className="dt-notice" role="status" aria-live="polite">{notice && <><FaCheck /> {notice}</>}</div>
        {!selectedRecord && !error && <section className="dt-empty" aria-busy={loading}>
          <FaLayerGroup /><h2>{loading ? 'Loading device metadata…' : hasSearched ? 'No records found' : 'Start with a device'}</h2>
          <p>{loading ? 'Retrieving captures for your selected date range.' : hasSearched ? 'Try a wider date range or check the device reference.' : 'Search a visor ref or device ID, then browse fields, compare captures, or export a payload.'}</p>
        </section>}
        {selectedRecord && <>
          <section className="dt-record-panel" aria-label="Capture selection">
            <div className="dt-record-title"><span className="dt-device-dot" /><strong>{deviceLabel}</strong><span>{records.length} capture{records.length === 1 ? '' : 's'} loaded</span></div>
            <div className="dt-record-controls">
              <label htmlFor="dt-selected-capture"><span>Selected capture</span><Select {...captureSelectProps}
                aria-label="Selected capture" inputId="dt-selected-capture" options={captureOptions}
                value={captureOptions.find((option) => option.value === selectedRecord.id)}
                onChange={(option) => chooseRecord(option.value)} />
              </label>
              <div className="dt-record-arrows"><button className="dt-icon-button" aria-label="Newer capture" disabled={selectedIndex === 0} onClick={() => chooseRecord(records[selectedIndex - 1].id)}><FaArrowLeft /></button>
                <span>{selectedIndex + 1} / {records.length}</span><button className="dt-icon-button" aria-label="Older capture" disabled={selectedIndex === records.length - 1} onClick={() => chooseRecord(records[selectedIndex + 1].id)}><FaArrowRight /></button></div>
              <label htmlFor="dt-comparison-capture"><span>Compare with</span><Select {...captureSelectProps}
                aria-label="Compare with" inputId="dt-comparison-capture" isDisabled={records.length < 2}
                options={[{ value: '', label: records.length < 2 ? 'Only one capture available' : 'No comparison' }, ...captureOptions.filter((option) => option.value !== selectedRecord.id)]}
                value={captureOptions.find((option) => option.value === comparison?.id) || { value: '', label: records.length < 2 ? 'Only one capture available' : 'No comparison' }}
                onChange={(option) => { setComparisonId(option.value); if (!option.value) setChangedOnly(false); }} />
              </label>
            </div>
            <div className="dt-capture-meta"><span><strong>{totalFields}</strong> payload fields</span><span><strong>{sections.length}</strong> sections</span><span>Times shown in {Intl.DateTimeFormat().resolvedOptions().timeZone}</span>
              {comparison && <span className="dt-change-summary"><strong>{changes}</strong> differences from {formatCaptureTime(comparison.timestamp)}</span>}</div>
          </section>
          <section className="dt-inspector" aria-label="Metadata explorer">
            <div className="dt-inspector-header"><div className="dt-view-switch" role="group" aria-label="Metadata view">
              <button aria-pressed={view === 'fields'} onClick={() => setView('fields')}><FaTable /> Fields</button>
              <button aria-pressed={view === 'json'} onClick={() => setView('json')}><FaCode /> Raw JSON</button></div>
              <div className="dt-export-actions"><button className="dt-button" onClick={() => copy(rawJson, 'Payload JSON')}><FaCopy /> Copy JSON</button>
                <button className="dt-button" onClick={() => download(rawJson, 'json', 'application/json')}><FaDownload /> Export JSON</button>
                {view === 'fields' && <button className="dt-button" disabled={!filteredFields.length} onClick={() => download('\uFEFF' + telemetryCsv(filteredFields), 'csv', 'text/csv;charset=utf-8')}><FaDownload /> Export fields (CSV)</button>}</div>
            </div>
            {view === 'json' ? <div className="dt-raw-view"><p>Full selected payload · unfiltered · original values. Export JSON includes this payload.</p><pre tabIndex={0} aria-label="Raw telemetry JSON">{rawJson}</pre></div> : <>
              <div className="dt-filterbar"><label className="dt-field-search"><span className="dt-sr-only">Search metadata</span><FaSearch aria-hidden="true" /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a field, package name, or value…" />{query && <button className="dt-icon-button" aria-label="Clear metadata search" onClick={() => setQuery('')}><FaTimes /></button>}</label>
                <label className="dt-type-filter"><span className="dt-sr-only">Field type</span><select value={type} onChange={(e) => setType(e.target.value)}><option value="">All types</option>{['string', 'number', 'boolean', 'null', 'array', 'object'].map((value) => <option key={value} value={value}>{sectionLabel(value)}</option>)}</select></label>
                <label className="dt-checkbox"><input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} /> Hide empty</label>
                {comparison && <label className="dt-checkbox"><input type="checkbox" checked={changedOnly} onChange={(e) => setChangedOnly(e.target.checked)} /> Differences only</label>}
                {activeFilters && <button className="dt-text-button" onClick={resetFilters}>Reset filters</button>}
              </div>
              {comparison && <p className="dt-comparison-note">Comparing fields by exact path, including array positions. Added and removed refer to the selected capture relative to the comparison.</p>}
              <div className="dt-explorer-layout"><nav className="dt-sections" aria-label="Telemetry sections">
                <div className="dt-section-heading"><FaFilter /> Sections</div>
                <button aria-pressed={!group} onClick={() => setGroup('')}><span>All fields</span><span>{fields.length}</span></button>
                {sections.map(([key, count]) => <button key={key} aria-pressed={group === key} onClick={() => setGroup(key)}><span>{sectionLabel(key)}</span><span>{count}</span></button>)}
              </nav><div className="dt-fields-panel"><div className="dt-fields-heading"><h2>{group ? sectionLabel(group) : 'All metadata'}</h2><span>{filteredFields.length} of {fields.length} fields</span></div>
                <div className="dt-table-scroll" ref={tableScroll}>
                  {filteredFields.length ? <table className="dt-table"><thead><tr><th scope="col">Field / path</th><th scope="col">{comparison ? 'Selected value / comparison' : 'Value'}</th><th scope="col"><span className="dt-sr-only">Copy actions</span></th></tr></thead>
                    <tbody>{visibleFields.map((field) => <TelemetryFieldRow key={field.path} field={field} onCopy={copy} imageRecord={isImageRecord(selectedRecord.entry)} previousImageRecord={isImageRecord(comparison?.entry)} />)}</tbody></table> : <div className="dt-no-matches"><FaSearch /><h3>No matching fields</h3><p>Try another keyword or reset your filters.</p><button className="dt-button" onClick={resetFilters}>Reset filters</button></div>}
                </div>
                <div className="dt-table-footer"><span>{filteredFields.length ? `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, filteredFields.length)} of ${filteredFields.length}` : '0 fields'} · CSV exports all matching fields</span>
                  {totalPages > 1 && <div className="dt-pagination"><button className="dt-icon-button" disabled={currentPage === 1} aria-label="Previous fields page" onClick={() => setPage(currentPage - 1)}><FaArrowLeft /></button><span>{currentPage} / {totalPages}</span><button className="dt-icon-button" disabled={currentPage === totalPages} aria-label="Next fields page" onClick={() => setPage(currentPage + 1)}><FaArrowRight /></button></div>}</div>
              </div></div>
            </>}
          </section>
        </>}
      </main>
    </div>
  );
  return embedded ? content : <div className="sd-page sd-telemetry-page">{content}</div>;
}

function TelemetryFieldRow({ field, onCopy, imageRecord, previousImageRecord }) {
  const { path, value, type, change, previousValue } = field;
  const imageSource = change === 'removed' ? '' : field.imageSource || telemetryImageSource(path, value, imageRecord);
  const previousImageSource = field.previousImageSource || telemetryImageSource(path, previousValue, previousImageRecord);
  const displayValue = change === 'removed' ? 'Not present in selected capture' : exactValue(value);
  const key = path.slice(path.lastIndexOf('.') + 1);
  return <tr className={change && change !== 'unchanged' ? `dt-row-${change}` : ''}>
    <th scope="row"><div className="dt-field-name">{key}<span className={`dt-type dt-type-${type}`}>{type}</span>{change && change !== 'unchanged' && <span className={`dt-change dt-change-${change}`}>{change}</span>}</div><code className="dt-field-path">{path}</code></th>
    <td><PreviewableFieldValue value={displayValue} source={imageSource} path={path} />{(change === 'changed' || change === 'removed') && <div className="dt-previous-value"><span>Comparison · {fieldType(previousValue)}</span><PreviewableFieldValue value={exactValue(previousValue)} source={previousImageSource} path={`${path} (comparison)`} /></div>}</td>
    <td><div className="dt-row-actions"><button className="dt-icon-button" title="Copy field path" aria-label={`Copy path for ${path}`} onClick={() => onCopy(path, 'Field path')}><FaCode /></button><button className="dt-icon-button" title="Copy exact JSON value" aria-label={`Copy value for ${path}`} disabled={change === 'removed'} onClick={() => onCopy(exactValue(value), 'JSON value')}><FaCopy /></button></div></td>
  </tr>;
}

function PreviewableFieldValue({ value, source, path }) {
  return source ? <TelemetryImagePreview key={source} source={source} path={path}><code className="dt-field-value">{value}</code></TelemetryImagePreview> : <FieldValue value={value} />;
}

function FieldValue({ value }) {
  if (value.length > 180) return <details className="dt-long-value"><summary>{value.slice(0, 140)}… <span>Show full value</span></summary><code className="dt-field-value">{value}</code></details>;
  return <code className="dt-field-value">{value}</code>;
}
